/**
 * Timeline helpers: map raw MatrixEvents into a simple, immutable message
 * model the UI can render, plus pagination + read-receipt utilities.
 */
import type { MatrixClient, MatrixEvent, Room } from "matrix-js-sdk";

export type MessageKind = "text" | "audio";

export interface AudioMeta {
  url: string | null; // resolved http(s) URL (media served unauthenticated)
  durationMs: number;
  mimetype: string;
  size: number;
  waveform: number[];
}

export interface ChatMessage {
  id: string;
  sender: string;
  body: string;
  ts: number;
  kind: MessageKind;
  isMine: boolean;
  audio?: AudioMeta;
}

/** Resolve an mxc url to http for media playback (unauthenticated media). */
function resolveMediaUrl(client: MatrixClient, mxc: string | undefined): string | null {
  if (!mxc) return null;
  // Try legacy unauthenticated endpoint first (media served openly here).
  const url = client.mxcUrlToHttp(mxc, undefined, undefined, undefined, false, true, false);
  return url ?? client.mxcUrlToHttp(mxc, undefined, undefined, undefined, false, true, true);
}

/** Map a single MatrixEvent to a ChatMessage, or null if not renderable. */
export function toChatMessage(client: MatrixClient, event: MatrixEvent): ChatMessage | null {
  if (event.getType() !== "m.room.message") return null;
  const content = event.getContent();
  const id = event.getId();
  const sender = event.getSender();
  if (!id || !sender) return null;

  const base = {
    id,
    sender,
    ts: event.getTs(),
    isMine: sender === client.getUserId(),
  };

  if (content.msgtype === "m.audio") {
    const msc1767 = (content["org.matrix.msc1767.audio"] ?? {}) as {
      duration?: number;
      waveform?: number[];
    };
    const info = (content.info ?? {}) as { duration?: number; mimetype?: string; size?: number };
    return {
      ...base,
      kind: "audio",
      body: typeof content.body === "string" ? content.body : "Voice message",
      audio: {
        url: resolveMediaUrl(client, content.url as string | undefined),
        durationMs: msc1767.duration ?? info.duration ?? 0,
        mimetype: info.mimetype ?? "audio/webm",
        size: info.size ?? 0,
        waveform: Array.isArray(msc1767.waveform) ? msc1767.waveform : [],
      },
    };
  }

  if (content.msgtype === "m.text" && typeof content.body === "string") {
    return { ...base, kind: "text", body: content.body };
  }

  return null;
}

/** Snapshot the current live-timeline messages for a room (oldest first). */
export function getRoomMessages(client: MatrixClient, room: Room): ChatMessage[] {
  return room
    .getLiveTimeline()
    .getEvents()
    .map((ev) => toChatMessage(client, ev))
    .filter((m): m is ChatMessage => m !== null);
}

/**
 * Load older messages (scrollback). Resolves to `true` if more history may
 * still exist, `false` when the start of the timeline is reached.
 */
export async function paginateBack(client: MatrixClient, room: Room, limit = 30): Promise<boolean> {
  const timeline = room.getLiveTimeline();
  return client.paginateEventTimeline(timeline, { backwards: true, limit });
}

/** Send a read receipt for the latest event in a room, if any. */
export async function markRoomRead(client: MatrixClient, room: Room): Promise<void> {
  const events = room.getLiveTimeline().getEvents();
  const last = events[events.length - 1];
  if (last) {
    try {
      await client.sendReadReceipt(last);
    } catch {
      // non-fatal
    }
  }
}
